import { useCallback, useState } from 'react';
import { useAuth } from '../util/auth';
import { useRouter } from '../util/router';
import { toggleSeenShow, addShowToWantToSee } from '../util/db';
import { useUserShowsStore } from '../stores';
import { useToast } from '../components/ui/Toast';
import SaveShowModal from '../components/SaveShowModal';
import SeenNoteModal from '../components/SeenNoteModal';

export default function useShowActions({ onSeenToggle } = {}) {
  const auth = useAuth();
  const router = useRouter();
  const { seenShows, savedListShows, wantToSeeShows, setSeenShows, setWantToSeeShows, fetchUserShows } =
    useUserShowsStore();
  const { showSuccess } = useToast();
  const [savingShowId, setSavingShowId] = useState(null);
  const [noteShowId, setNoteShowId] = useState(null);

  const userId = auth.user?.id;

  const requireSignin = useCallback(() => {
    const next = `${router.location.pathname}${router.location.search}`;
    router.push(`/auth/signin?next=${encodeURIComponent(next)}`);
  }, [router]);

  const handleSeen = useCallback(
    async showId => {
      if (!userId) return requireSignin();
      try {
        const result = await toggleSeenShow(userId, showId);
        const next = new Set(useUserShowsStore.getState().seenShows);
        if (result.removed) next.delete(showId);
        if (result.added) next.add(showId);
        setSeenShows(next);
        if (result.added) {
          showSuccess('Marked as seen', {
            action: { label: 'Add note', onClick: () => setNoteShowId(showId) },
          });
        }
        onSeenToggle?.(showId, result);
        return result;
      } catch (error) {
        console.error(error);
      }
    },
    [userId, requireSignin, setSeenShows, showSuccess, onSeenToggle]
  );

  const handleSave = useCallback(
    showId => {
      if (!userId) return requireSignin();

      if (useUserShowsStore.getState().savedListShows.has(showId)) {
        setSavingShowId(showId);
        return;
      }

      const previous = useUserShowsStore.getState().wantToSeeShows;
      setWantToSeeShows(new Set([...previous, showId]));
      showSuccess('Saved to Want to See', {
        action: { label: 'Change', onClick: () => setSavingShowId(showId) },
      });

      addShowToWantToSee(userId, showId).catch(error => {
        console.error('Error saving to Want to See:', error);
        setWantToSeeShows(previous);
      });
    },
    [userId, requireSignin, setWantToSeeShows, showSuccess]
  );

  const modals = (
    <>
      {savingShowId != null && userId && (
        <SaveShowModal
          showId={savingShowId}
          onDone={() => {
            setSavingShowId(null);
            fetchUserShows(userId);
          }}
        />
      )}
      {noteShowId != null && userId && (
        <SeenNoteModal showId={noteShowId} onClose={() => setNoteShowId(null)} />
      )}
    </>
  );

  return {
    isSeen: showId => seenShows.has(showId),
    isSaved: showId => savedListShows.has(showId),
    isWantToSee: showId => wantToSeeShows.has(showId),
    handleSeen,
    handleSave,
    modals,
  };
}
